// =============================================================================
// Origin UI — src/pages/leave/MyLeavePage.tsx
// Employee leave dashboard: balance table, request list, public holidays.
// Phase 6 — Leave Management module.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaveApi, type LeaveBalance, type LeaveRequestListItem, type PublicHoliday } from '@/services/leaveApi';
import { PageHeader, Card, ErrorBanner, LoadingSpinner, StatusBadge } from '@/components/common';
import styles from './leave.module.css';

export function MyLeavePage() {
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [requests, setRequests] = useState<LeaveRequestListItem[]>([]);
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'requests' | 'history' | 'holidays'>('requests');
  const [historyYear, setHistoryYear] = useState<number | 'all'>(new Date().getFullYear());
  const [historyStatus, setHistoryStatus] = useState<string>('all');
  const navigate = useNavigate();

  // Cancel / Delete confirmation state
  const [confirmAction, setConfirmAction] = useState<{ id: string; type: 'cancel' | 'delete'; label: string } | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [acting, setActing] = useState(false);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError('');
      const [bal, req, hol] = await Promise.all([
        leaveApi.getBalance(),
        leaveApi.getMyRequests(),
        leaveApi.getHolidays(),
      ]);
      setBalances(bal);
      setRequests(req);
      setHolidays(hol);
    } catch (err: any) {
      setError(err.message || 'Failed to load leave data');
    } finally {
      setLoading(false);
    }
  }

  function canCancel(r: LeaveRequestListItem): boolean {
    if (r.status === 'Submitted') return true;
    if (r.status === 'Approved' && new Date(r.startDate) > new Date()) return true;
    return false;
  }

  function canDelete(r: LeaveRequestListItem): boolean {
    return r.status === 'Draft';
  }

  function canEdit(r: LeaveRequestListItem): boolean {
    return r.status === 'Draft' || r.status === 'Submitted';
  }

  // Split requests into the live list (My Requests) and the closed archive (History).
  // Closed = Rejected / Cancelled / Revoked, or Approved leave that has already ended.
  function isClosed(r: LeaveRequestListItem): boolean {
    if (r.status === 'Rejected' || r.status === 'Cancelled' || r.status === 'Revoked') return true;
    if (r.status === 'Approved' && new Date(r.endDate) < startOfToday()) return true;
    return false;
  }

  function startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function openConfirm(e: React.MouseEvent, id: string, type: 'cancel' | 'delete', label: string) {
    e.stopPropagation(); // Don't navigate to the detail page
    setConfirmAction({ id, type, label });
    setCancelReason('');
  }

  async function handleConfirmedAction() {
    if (!confirmAction) return;
    try {
      setActing(true);
      setError('');
      await leaveApi.cancelRequest(confirmAction.id, cancelReason || undefined);
      setConfirmAction(null);
      setCancelReason('');
      // Reload data to reflect the change
      await loadData();
    } catch (err: any) {
      setError(err.message || `Failed to ${confirmAction.type} request`);
      setConfirmAction(null);
    } finally {
      setActing(false);
    }
  }

  const activeRequests = requests.filter((r) => !isClosed(r));
  const closedRequests = requests.filter((r) => isClosed(r));
  const historyYears = Array.from(
    new Set(closedRequests.map((r) => new Date(r.startDate).getFullYear()))
  ).sort((a, b) => b - a);
  const historyRequests = closedRequests.filter((r) => {
    if (historyYear !== 'all' && new Date(r.startDate).getFullYear() !== historyYear) return false;
    if (historyStatus !== 'all' && r.status !== historyStatus) return false;
    return true;
  });

  if (loading) return <LoadingSpinner message="Loading leave data..." />;

  // Hide balance rows for notification-type leave (e.g. Sick, Compassionate).
  // The leave type stays bookable via the request form (separate /types endpoint).
  const visibleBalances = balances.filter((b) => !b.balanceHidden);

  return (
    <div className={styles.page}>
      <PageHeader
        title="My Leave"
        subtitle={`${new Date().getFullYear()} leave balances and requests`}
        action={
          <button className={styles.createBtn} onClick={() => navigate('/leave/request')}>
            + New Request
          </button>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* ─── Confirmation Dialog ─── */}
      {confirmAction && (
        <div className={styles.confirmOverlay} onClick={() => setConfirmAction(null)}>
          <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>
              {confirmAction.type === 'delete' ? 'Delete Draft' : 'Cancel Leave Request'}
            </h3>
            <p className={styles.confirmMessage}>
              {confirmAction.type === 'delete'
                ? 'Are you sure you want to delete this draft? This cannot be undone.'
                : `Are you sure you want to cancel this ${confirmAction.label}? The days will be returned to your balance.`
              }
            </p>
            {confirmAction.type === 'cancel' && (
              <div className={styles.formGroup} style={{ marginTop: 12 }}>
                <label className={styles.formLabel}>Reason (optional)</label>
                <textarea
                  className={styles.formTextarea}
                  rows={2}
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="Why are you cancelling this request?"
                />
              </div>
            )}
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setConfirmAction(null)} disabled={acting}>
                Go Back
              </button>
              <button className={styles.btnDanger} onClick={handleConfirmedAction} disabled={acting}>
                {acting ? 'Processing...' : confirmAction.type === 'delete' ? 'Delete Draft' : 'Cancel Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Balance Table ─── */}
      <Card padding={false}>
        <table className={styles.balanceTable}>
          <thead>
            <tr>
              <th>Leave Type</th>
              <th className={styles.numCol}>Entitlement</th>
              <th className={styles.numCol}>Carry Fwd</th>
              <th className={styles.numCol}>Taken</th>
              <th className={styles.numCol}>Committed</th>
              <th className={styles.numCol}>Pending</th>
              <th className={styles.numCol}>Available</th>
            </tr>
          </thead>
          <tbody>
            {visibleBalances.map((b) => (
              <tr key={b.leaveTypeId}>
                <td>
                  <span className={styles.leaveTypeTag}>{b.leaveTypeCode}</span>
                  {b.leaveTypeName}
                  {b.isPaid && <span className={styles.paidBadge}>Paid</span>}
                </td>
                <td className={styles.numCol}>{b.entitlement}</td>
                <td className={styles.numCol}>{b.carryForward > 0 ? b.carryForward : '—'}</td>
                <td className={`${styles.numCol} ${b.taken > 0 ? styles.taken : ''}`}>{b.taken > 0 ? b.taken : '—'}</td>
                <td className={`${styles.numCol} ${b.committed > 0 ? styles.committed : ''}`}>{b.committed > 0 ? b.committed : '—'}</td>
                <td className={`${styles.numCol} ${b.pending > 0 ? styles.pending : ''}`}>{b.pending > 0 ? b.pending : '—'}</td>
                <td className={`${styles.numCol} ${styles.availableCol}`}>{b.available}</td>
              </tr>
            ))}
            {visibleBalances.length === 0 && (
              <tr><td colSpan={7} className={styles.emptyTab}>No leave entitlements configured. Please contact HR.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* ─── Tabs: Requests / Holidays ─── */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'requests' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          My Requests ({activeRequests.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'history' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Leave History ({closedRequests.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'holidays' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('holidays')}
        >
          Public Holidays ({holidays.length})
        </button>
      </div>

      {/* ─── Requests Tab ─── */}
      {activeTab === 'requests' && (
        <Card padding={false}>
          {activeRequests.length === 0 ? (
            <div className={styles.emptyTab}>
              <p>No leave requests yet.</p>
              <button className={styles.createBtn} onClick={() => navigate('/leave/request')}>
                Request Leave
              </button>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeRequests.map((r) => (
                  <tr key={r.leaveRequestId} onClick={() => navigate(`/leave/requests/${r.leaveRequestId}`)} className={styles.clickableRow}>
                    <td><span className={styles.leaveTypeTag}>{r.leaveTypeCode}</span> {r.leaveTypeName}</td>
                    <td>{new Date(r.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{new Date(r.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{r.totalWorkingDays}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>{new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                    <td>
                      {canEdit(r) && (
                        <button
                          className={styles.btnInlineEdit}
                          onClick={e => { e.stopPropagation(); navigate(`/leave/requests/${r.leaveRequestId}`); }}
                        >
                          Edit
                        </button>
                      )}
                      {canDelete(r) && (
                        <button
                          className={styles.btnInlineDelete}
                          onClick={e => openConfirm(e, r.leaveRequestId, 'delete', r.leaveTypeName)}
                        >
                          Delete
                        </button>
                      )}
                      {canCancel(r) && (
                        <button
                          className={styles.btnInlineCancel}
                          onClick={e => openConfirm(e, r.leaveRequestId, 'cancel', `${r.leaveTypeName} request`)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* ─── Leave History Tab ─── */}
      {activeTab === 'history' && (
        <Card padding={false}>
          <div style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
            <select
              className={styles.filterSelect}
              value={historyYear === 'all' ? 'all' : String(historyYear)}
              onChange={(e) => setHistoryYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            >
              <option value="all">All years</option>
              {historyYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select
              className={styles.filterSelect}
              value={historyStatus}
              onChange={(e) => setHistoryStatus(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Revoked">Revoked</option>
            </select>
          </div>
          {historyRequests.length === 0 ? (
            <div className={styles.emptyTab}>
              <p>No leave history matches these filters.</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {historyRequests.map((r) => (
                  <tr key={r.leaveRequestId} onClick={() => navigate(`/leave/requests/${r.leaveRequestId}`)} className={styles.clickableRow}>
                    <td><span className={styles.leaveTypeTag}>{r.leaveTypeCode}</span> {r.leaveTypeName}</td>
                    <td>{new Date(r.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{new Date(r.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{r.totalWorkingDays}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>{new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* ─── Holidays Tab ─── */}
      {activeTab === 'holidays' && (
        <Card padding={false}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Holiday</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => {
                const date = new Date(h.date);
                const dayName = date.toLocaleDateString('en-GB', { weekday: 'long' });
                const isPast = date < new Date();
                return (
                  <tr key={h.publicHolidayId} className={isPast ? styles.pastHoliday : ''}>
                    <td>{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{dayName}</td>
                    <td>{h.name}{h.isHalfDay && <span className={styles.halfDayBadge}>Half day</span>}</td>
                    <td className={styles.muted}>{h.notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
